import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { restClient } from "@/src/services/restClient";
const eventsAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.title.localeCompare(b.title)
});
const initialState = eventsAdapter.getInitialState({
  status: "idle",
  error: null
});
const toMessage = (error, fallback) => {
  if (typeof error === "string" && error.trim()) return error;
  if (error instanceof Error) return error.message;
  return fallback;
};
export const fetchEvents = createAsyncThunk("events/fetchAll", async (_, {
  rejectWithValue
}) => {
  try {
    return await restClient.get("/events");
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to load events"));
  }
});
export const createEventThunk = createAsyncThunk("events/create", async (payload, {
  rejectWithValue
}) => {
  try {
    return await restClient.post("/events", payload);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to create event"));
  }
});
export const updateEventThunk = createAsyncThunk("events/update", async ({
  id,
  patch
}, {
  rejectWithValue
}) => {
  try {
    return await restClient.patch(`/events/${id}`, patch);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to update event"));
  }
});
export const deleteEventThunk = createAsyncThunk("events/delete", async (id, {
  rejectWithValue
}) => {
  try {
    await restClient.delete(`/events/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to delete event"));
  }
});
const eventsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    clearEventsError(state) {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchEvents.pending, state => {
      state.status = "loading";
      state.error = null;
    }).addCase(fetchEvents.fulfilled, (state, action) => {
      state.status = "succeeded";
      eventsAdapter.setAll(state, action.payload);
    }).addCase(fetchEvents.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload ?? action.error.message ?? null;
    }).addCase(createEventThunk.fulfilled, (state, action) => {
      eventsAdapter.upsertOne(state, action.payload);
    }).addCase(updateEventThunk.fulfilled, (state, action) => {
      eventsAdapter.upsertOne(state, action.payload);
    }).addCase(deleteEventThunk.fulfilled, (state, action) => {
      eventsAdapter.removeOne(state, action.payload);
    });
  }
});
export const {
  clearEventsError
} = eventsSlice.actions;
export const eventsSelectors = eventsAdapter.getSelectors(state => state.events);
export const selectEventsList = eventsSelectors.selectAll;
export const selectEventsStatus = state => state.events.status;
export const selectEventsError = state => state.events.error;
export default eventsSlice.reducer;